import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Loading, Title } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  FetchApprovalGroupsDocument,
  FetchBlockedPeriodsDocument,
  FetchLeaveTypesDocument,
  FetchTimeoffPoliciesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import ApprovalsBlock from 'modules/approvals/components/ApprovalsConfigurationBlock'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import BlockedPeriodsSettingsBlock from 'modules/timeoff/components/BlockedPeriodsSettings/Block'
import LeaveTypesBlock from 'modules/timeoff/components/LeaveTypeSettings/LeaveTypesBlock'
import TimeoffPolicies from 'modules/timeoff/components/TimeoffPolicies'
import { TabTypes } from 'modules/timeoff/lib/TimeoffSettings'

interface TableContentProps {
  tabId?: string
}

const TableContent = ({ tabId }: TableContentProps) => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  // Fetch data based on active tab
  const policiesQuery = useQuery(FetchTimeoffPoliciesDocument, {
    variables: {
      companyIds: [company.id],
    },
    skip: tabId !== TabTypes.policies,
  })

  const leaveTypesQuery = useQuery(FetchLeaveTypesDocument, {
    variables: {
      companyIds: [company.id],
    },
    skip: tabId !== TabTypes.absenceTypes,
  })

  const approvalGroupsQuery = useQuery(FetchApprovalGroupsDocument, {
    variables: {
      featureIds: [Features.timeoff_management, Features.timeoff_blocked_periods],
    },
    skip: tabId !== TabTypes.approvalGroups && tabId !== TabTypes.blockedPeriods,
  })

  const blockedPeriodsQuery = useQuery(FetchBlockedPeriodsDocument, {
    variables: {
      companyIds: [company.id],
    },
    skip: tabId !== TabTypes.blockedPeriods,
  })

  // Fetch leaveTypes for blocked periods tab (needed for defaultLeaveTypeId)
  const blockedPeriodsLeaveTypesQuery = useQuery(FetchLeaveTypesDocument, {
    variables: {
      companyIds: [company.id],
    },
    skip: tabId !== TabTypes.blockedPeriods,
  })

  // Determine loading state based on active tab
  const loading =
    (tabId === TabTypes.policies && policiesQuery.loading) ||
    (tabId === TabTypes.absenceTypes && leaveTypesQuery.loading) ||
    (tabId === TabTypes.approvalGroups && approvalGroupsQuery.loading) ||
    (tabId === TabTypes.blockedPeriods &&
      (blockedPeriodsQuery.loading || blockedPeriodsLeaveTypesQuery.loading))

  if (loading) {
    return (
      <ResponsivePage.Body showHeader={false}>
        <Loading />
      </ResponsivePage.Body>
    )
  }

  switch (tabId) {
    case TabTypes.policies: {
      const policies = policiesQuery.data?.timeoff.policiesConnection?.nodes || []
      const timeoffPoliciesTotal = policiesQuery.data?.timeoff.policiesConnection?.totalCount
      return (
        <Box paddingY="s24">
          <Box gap="s24">
            <Title
              title={i18n.t('settings.timeoff_policies.list.title')}
              description={i18n.t('settings.timeoff_policies.list.description')}
              illustration="calendar-clock"
              variant="500"
            />
            <Button
              iconName="add"
              onClick={() => navigate(navigation.settings.timeoffPolicies.new.fullPath())}
            >
              {i18n.t('settings.timeoff_policies.add')}
            </Button>
          </Box>
          <Box paddingY="s24" extra={{ 'data-intercom-target': 'timeoff-policies-list' }}>
            <TimeoffPolicies timeoffPolicies={policies} totalCount={timeoffPoliciesTotal} />
          </Box>
        </Box>
      )
    }

    case TabTypes.absenceTypes: {
      const leaveTypes = leaveTypesQuery.data?.timeoff.leaveTypes || []
      return (
        <Box paddingY="s24">
          <LeaveTypesBlock loading={leaveTypesQuery.loading} data={leaveTypes} />
        </Box>
      )
    }

    case TabTypes.approvalGroups:
      return (
        <Box paddingY="s24">
          <ApprovalsBlock
            category="timeoff"
            title={i18n.t('settings.time_off.approvals.title')}
            description={i18n.t('settings.time_off.approvals.description')}
            helpCenterUrlName="timeoff_approvals"
            noPadding
          />
        </Box>
      )

    case TabTypes.blockedPeriods: {
      const blockedPeriodsPolicies =
        blockedPeriodsQuery.data?.timeoff.blockedPeriodsConnection?.nodes || []
      const blockedPeriodsPoliciesTotal =
        blockedPeriodsQuery.data?.timeoff.blockedPeriodsConnection?.totalCount
      const canManageTimeOff =
        approvalGroupsQuery.data?.timeoff._policies.companyManageTimeoff.can ?? false
      const leaveTypes = blockedPeriodsLeaveTypesQuery.data?.timeoff.leaveTypes || []
      const blockedPeriodsEnabled =
        approvalGroupsQuery.data?.companies.featureAvailabilitiesConnection.nodes.find(
          (f) => f.id === Features.timeoff_blocked_periods
        )?.available ?? false

      return (
        <>
          {blockedPeriodsEnabled && (
            <Box paddingY="s24">
              <BlockedPeriodsSettingsBlock
                title={i18n.t('settings.time_off.blocked_periods.title')}
                description={i18n.t('settings.time_off.blocked_periods.description')}
                helpCenterUrlName="timeoff_blocked_periods"
                data={blockedPeriodsPolicies}
                totalCount={blockedPeriodsPoliciesTotal}
                defaultLeaveTypeId={leaveTypes[0]?.id || 0}
                canManageTimeOff={canManageTimeOff}
              />
            </Box>
          )}
        </>
      )
    }

    default:
      return null
  }
}

export default TableContent
